import React, { CSSProperties } from "react";

interface FeatureCardExportProps {
  title?: string;
  description?: string;
  imageUrl?: string;
  imageHeight?: number;
  category?: string;
  categoryColor?: string;
  link?: string;
  linkText?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  showShadow?: boolean;
  textFontFamily?: string;
}

export const FeatureCardExport: React.FC<FeatureCardExportProps> = ({
  title = "Feature Title",
  description = "Description of this feature",
  imageUrl,
  imageHeight = 60,
  category,
  categoryColor = "#F2819F",
  link,
  linkText = "Learn more",
  backgroundColor = "#ffffff",
  textColor = "#2C2C2B",
  borderRadius = 12,
  showShadow = true,
  textFontFamily,
}) => {
  const hasThai = (s: string) => /[\u0E00-\u0E7F]/.test(s);
  const ffStyle = (text?: string): CSSProperties => textFontFamily ? { fontFamily: `'${textFontFamily}', var(--font-family), sans-serif` } : (text && !hasThai(text)) ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {};
  const cardStyles: CSSProperties = {
    backgroundColor,
    borderRadius: `${borderRadius}px`,
    boxShadow: showShadow ? "0px 0px 20px 0px rgba(0, 0, 0, 0.15)" : "none",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    minHeight: "195px",
  };

  return (
    <div style={cardStyles} className="feature-card" data-element="feature-item" data-type="content" data-category={category || ""}>
      {/* Title + Description */}
      <div style={{ padding: "20px", paddingBottom: "10px", paddingRight: imageUrl ? "80px" : "20px", display: "flex", flexDirection: "column", gap: "8px" }}>
        <h3 style={{ fontSize: "1.5rem", fontWeight: 700, color: textColor, lineHeight: 1.3, margin: 0, wordWrap: "break-word", ...ffStyle(title) }}>
          {title}
        </h3>
        <p style={{ fontSize: "1rem", color: textColor, opacity: 0.7, lineHeight: 1.6, margin: 0, wordWrap: "break-word", ...ffStyle(description) }}>
          {description}
        </p>
      </div>

      {/* Image — bottom right decorative */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt={title}
          style={{
            position: "absolute",
            bottom: "0",
            right: "8px",
            width: `${(imageHeight || 60) + 36}px`,
            height: "auto",
            maxHeight: `${(imageHeight || 60) + 36}px`,
            objectFit: "contain",
          }}
        />
      )}

      {/* Link */}
      {link && (
        <div style={{ padding: "4px 20px 0" }}>
          <a
            href={link}
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: categoryColor,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span style={ffStyle(linkText)}>{linkText}</span> &gt;
          </a>
        </div>
      )}

      {/* Category badge — absolute bottom left */}
      {category && (
        <span
          style={{
            position: "absolute",
            bottom: "20px",
            left: "20px",
            display: "inline-block",
            background: `linear-gradient(90deg, ${categoryColor}22 0%, ${categoryColor}44 100%)`,
            color: categoryColor,
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: "4px 12px",
            borderRadius: "999px",
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            ...ffStyle(category),
          }}
        >
          {category}
        </span>
      )}
    </div>
  );
};
